/**
 * @file   src\components\SignTemplate01.tsx
 * @brief  SignTemplate component.
 * @date   AUGUST, 2025
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import {
  React,
  useState,
  Button,
  useEffect,
  Row,
} from "../utils/thirdpartyComponents";
import "../styles/signtemplate.scss";
import QRCode from "qrcode.react";
import Scanarrow from "../assets/images/theme1-caret-up.svg";
import html2canvas from "html2canvas";
import { toPng, toJpeg } from "html-to-image";
import jsPDF from "jspdf";
import FullLoader from "../components/FullLoader";
import CWCheckbox from "./CWCheckbox";

const SignTemplate01 = (props: any) => {

  const [loading, setLoading] = useState(false);
  const [companyLogoBase64, setCompanyLogoBase64] = useState<string | null>(
    null
  );
  const [agreeTerms, setAgreeTerms] = useState<boolean>(false);
  

  useEffect(() => {
    setCompanyLogoBase64(props.companyLogoUrl);
    // convertImageToBase64(props.companyLogoUrl)
    //     .then((base64) => {
    //         setCompanyLogoBase64(base64);
    //     })
    //     .catch((err) => {
    //         console.error("Failed to load image:", err);
    //     });
  }, [props.companyLogoUrl]);
  const captureId = "capture";

  // Download as PDF
  const downloadAsPDF = async () => {
    setLoading(true);
    const element = document.getElementById(captureId);
    if (!element) return;

    // Convert inches to millimeters
    const pdfWidthMM = 203.2;   // 8 inches
    const pdfHeightMM = 304.8; // 12 inches

    // Set pixel dimensions for high DPI (300 DPI)
    const targetWidth = 2400;
    const targetHeight = 3600;

    if (navigator.userAgent.toLowerCase().includes("firefox")) {
      try {
        const canvas = await html2canvas(element, {
          scale: 1, // we'll resize manually
          useCORS: true,
          logging: false,
          width: targetWidth,
          height: targetHeight,
        });

        const resizedCanvas = document.createElement("canvas");
        resizedCanvas.width = targetWidth;
        resizedCanvas.height = targetHeight;

        const ctx = resizedCanvas.getContext("2d");
        ctx?.drawImage(canvas, 0, 0, targetWidth, targetHeight);

        const imgData = resizedCanvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: [pdfWidthMM, pdfHeightMM],
        });

        pdf.addImage(imgData, "PNG", 0, 0, pdfWidthMM, pdfHeightMM);
        pdf.save(`${props.washname}-template-1.pdf`);
      } catch (error) {
        console.error("Error generating high-quality PDF:", error);
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const dataUrl = await toPng(element, {
          quality: 1.0,
          canvasWidth: targetWidth,
          canvasHeight: targetHeight,
          pixelRatio: 1, // manually setting width/height, so keep ratio 1
          cacheBust: true,
        });

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: [pdfWidthMM, pdfHeightMM],
        });

        pdf.addImage(dataUrl, "PNG", 0, 0, pdfWidthMM, pdfHeightMM);
        pdf.save(`${props.washname}-template-1.pdf`);
      } catch (error) {
        console.error("Error generating high-quality PDF:", error);
      } finally {
        setLoading(false);
      }
    }
  };


  // Download as JPG
  const downloadAsJPG = async () => {
    setLoading(true);
    const element = document.getElementById(captureId);
    if (!element) return;

    const targetWidth = 2400; // 8 inches at 300 DPI
    const targetHeight = 3600; // 12 inches at 300 DPI

    if (navigator.userAgent.toLowerCase().includes("firefox")) {
      try {
        const canvas = await html2canvas(element, {
          scale: 1, // Keep scale 1 to let us control canvas size manually
          useCORS: true,
          logging: false,
          width: targetWidth,
          height: targetHeight,
        });

        const resizedCanvas = document.createElement("canvas");
        resizedCanvas.width = targetWidth;
        resizedCanvas.height = targetHeight;

        const ctx = resizedCanvas.getContext("2d");
        ctx?.drawImage(canvas, 0, 0, targetWidth, targetHeight);

        const link = document.createElement("a");
        link.download = `${props.washname}-template-1.jpg`;
        link.href = resizedCanvas.toDataURL("image/jpeg", 1.0);
        link.click();
      } catch (error) {
        console.error("Error generating high-quality JPG:", error);
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const dataUrl = await toJpeg(element, {
          quality: 1.0,
          canvasWidth: targetWidth,
          canvasHeight: targetHeight,
          pixelRatio: 1, // Already specifying the dimensions directly
          cacheBust: true,
        });

        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${props.washname}-template-1.jpg`;
        link.click();
      } catch (error) {
        console.error("Error generating high-quality JPG:", error);
      } finally {
        setLoading(false);
      }
    }
  };


  // Download as PNG
  const downloadAsPNG = async () => {
    setLoading(true);
    const element = document.getElementById(captureId);
    if (!element) return;

    // Target export dimensions: 8" x 12" @ 300 DPI → 2400px x 3600px
    const targetWidth = 2400;
    const targetHeight = 3600;

    if (navigator.userAgent.toLowerCase().includes("firefox")) {
      try {
        const canvas = await html2canvas(element, {
          scale: 1,
          useCORS: true,
          logging: false,
          width: targetWidth,
          height: targetHeight,
        });

        // Resize to exact dimensions
        const resizedCanvas = document.createElement("canvas");
        resizedCanvas.width = targetWidth;
        resizedCanvas.height = targetHeight;
        const ctx = resizedCanvas.getContext("2d");
        ctx?.drawImage(canvas, 0, 0, targetWidth, targetHeight);

        const link = document.createElement("a");
        link.download = `${props.washname}-template-1.png`;
        link.href = resizedCanvas.toDataURL("image/png");
        link.click();
      } catch (error) {
        console.error("Error generating high-quality PNG:", error);
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const dataUrl = await toPng(element, {
          cacheBust: true,
          pixelRatio: 1,
          canvasWidth: targetWidth,
          canvasHeight: targetHeight,
        });

        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${props.washname}-template-1.png`;
        link.click();
      } catch (error) {
        console.error("Error generating high-quality PNG:", error);
      } finally {
        setLoading(false);
      }
    }
  };


  return (
    <div>
      <div id={captureId} className="template-first">
        <div className="template-header text-center position-relative">
          <div className="header-bg"></div>
          <div className="logo d-flex align-items-center justify-content-center mx-auto">
            {companyLogoBase64 ? (
              <img src={companyLogoBase64} alt="Car Wash App" />
            ) : (
              <span>Loading logo...</span>
            )}
          </div>
          <h1
            className="f-extrabold"
            style={{ fontSize: `${props.washnamefontsize}` }}
          >
            {props.washname}
          </h1>
        </div>
        <div className="template-content text-center position-relative">
          <div className="bay-name f-bold mx-auto">{props.bayName}</div>
          <div className="qr-scanner mx-auto">
            <QRCode
              value={props.bayId}
              size={190}
              level={"H"}
              includeMargin={true}
              renderAs="svg"
              crossOrigin="anonymous"
            />
          </div>
          <div className="scan-text f-extrabold d-flex align-items-center flex-column justify-content-center">
            <img src={Scanarrow} alt="Car Wash App" />
            Scan in the App to Wash
          </div>
          <div className="main-title mx-auto">
            <h1 className="f-extrabold position-relative">
              {props.titlecontent}
            </h1>
          </div>
          <div className="website-info mx-auto d-flex align-items-center justify-content-between">
            <div className="app-url text-start">
              <h3 className="f-extrabold">Scan to get the app</h3>
              <p className="f-medium">APP URL: {props.appUrl}</p>
            </div>
            <div className="scanner d-flex align-items-center justify-content-end">
              <div className="overlay">
                <QRCode
                  value={props.appUrl}
                  size={85}
                  level={"H"}
                  includeMargin={true}
                  renderAs="svg"
                  crossOrigin="anonymous"
                />
              </div>
            </div>
          </div>
          <div className="plan-information d-flex justify-content-center">
            <div className="info-card d-flex align-items-center position-relative">
              {/* <span className="arrow"></span> */}
              <h3
                className="f-extrabold mx-auto"
                style={{ fontSize: `${props.box1fontsize}` }}
              >
                {props.subscriptionbox1}
              </h3>
            </div>
            <div className="info-card d-flex align-items-center position-relative">
              {/* <span className="arrow"></span> */}
              <h3
                className="f-extrabold mx-auto"
                style={{ fontSize: `${props.box2fontsize}` }}
              >
                {props.subscriptionbox2}
              </h3>
            </div>
            <div className="info-card d-flex align-items-center position-relative">
              {/* <span className="arrow"></span> */}
              <h3
                className="f-extrabold mx-auto"
                style={{ fontSize: `${props.box3fontsize}` }}
              >
                {props.subscriptionbox3}
              </h3>
            </div>
          </div>
        </div>
      </div>
      {/* <p className='note mt-2'>Note: Please proofread your sign before downloading. </p> */}
      <div className="mt-3">
        <CWCheckbox
          id="agree"
          name="agree"
          label="By checking this box, I (carwash owner) acknowledge that I have proofread the sign and checked all QR codes.  Any reprints will be the responsibility of the carwash owner."
          checked={agreeTerms}
          // disabled={props.edit ? true : false}
          onChange={(event: any) => setAgreeTerms(event.target.checked)}
        />
      </div>
      <div className="btn-sec d-flex mt-4">
        <Button
          title="Download as PDF"
          variant="primary"
          className="primary-btn download-btn"
          type="button"
          disabled={props.appUrl == "" || agreeTerms === false}
          onClick={downloadAsPDF}
        >
          Download as PDF
        </Button>
        <Button
          title="Download as JPG"
          variant="primary"
          className="primary-btn download-btn"
          type="button"
          disabled={props.appUrl == "" || agreeTerms === false}
          onClick={downloadAsJPG}
        >
          Download as JPG
        </Button>
        <Button
          title="Download as PNG"
          variant="primary"
          className="primary-btn download-btn"
          type="button"
          disabled={props.appUrl == "" || agreeTerms === false}
          onClick={downloadAsPNG}
        >
          Download as PNG
        </Button>

        {/* <Button
            title="Download as EPS"
            variant="primary"
            className="primary-btn download-btn"
            type="button"
            onClick={downloadAsEPS}
        >
            Download as EPS
        </Button> */}
      </div>
      <div className="mt-3">
        <span className="error-msg">
          App URL need to be generated for Downloading the Sign.
        </span>
        </div>
        <div className="mt-1">
        <span className="error-msg">
        For best results use Chrome browser.         
        </span>
      </div>
      {loading && <FullLoader />}
    </div>
  );
};
export default SignTemplate01;
